import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { answerPreviewCapture, getUserAppDraft, type PreviewCaptureUpload } from '@api/appBuilder';
import { trackError } from '@utils/datadog';
import type { AppFramePreview } from '../../appFrame/AppFrame';
import { snapshotOfImage } from '../../viewSnapshot';
import { appBuilderKeys } from '../useAppBuilder';

/** How long the preview has to load the latest save; the kit's own settling comes on top. */
export const PREVIEW_LOAD_TIMEOUT_MS = 20_000;
const POLL_MS = 200;

// Read by the builder assistant, which acts on them.
export const NOT_SHOWING = 'The creator is not looking at the preview right now, so it could not be captured.';
export const NO_PAGE = 'The app has no page yet.';
export const EARLIER_PAGE =
  'The preview is still showing an earlier version of the page (a test run of it may be in progress), '
  + 'so your latest save could not be seen.';
export const NOT_LOADED = 'The preview did not finish loading your latest save in time.';
export const FAILED = 'The preview could not be captured.';

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const loadedPreview = async (
  previewRef: RefObject<AppFramePreview | null>,
  pageKey: string,
): Promise<AppFramePreview | string> => {
  const deadline = Date.now() + PREVIEW_LOAD_TIMEOUT_MS;
  for (;;) {
    const preview = previewRef.current;
    if (preview?.pageKey === pageKey && preview.loaded) return preview;
    if (Date.now() >= deadline) return preview && preview.pageKey !== pageKey ? EARLIER_PAGE : NOT_LOADED;
    await pause(POLL_MS);
  }
};

/**
 * Answers the builder assistant's asks to see the app: the preview once it
 * shows the latest save and has settled, with what its page reported.
 */
export const usePreviewCapture = (
  appKey: string,
  previewRef: RefObject<AppFramePreview | null>,
  showing: boolean,
) => {
  const queryClient = useQueryClient();
  const showingRef = useRef(showing);
  useEffect(() => {
    showingRef.current = showing;
  }, [showing]);

  const look = useCallback(async (): Promise<PreviewCaptureUpload> => {
    if (!showingRef.current) return { failure: NOT_SHOWING };
    const draft = await queryClient.fetchQuery({
      queryKey: appBuilderKeys.draft(appKey),
      queryFn: () => getUserAppDraft(appKey),
      staleTime: 0,
    });
    const pageKey = draft.preview.presentation?.page;
    if (!pageKey) return { failure: NO_PAGE };
    const preview = await loadedPreview(previewRef, pageKey);
    if (typeof preview === 'string') return { failure: preview };
    const { image, pageErrors } = await preview.capture();
    return { snapshot: await snapshotOfImage(image), pageErrors };
  }, [appKey, previewRef, queryClient]);

  return useCallback(
    (captureId: string) => {
      look()
        .catch((error: unknown): PreviewCaptureUpload => {
          trackError(error instanceof Error ? error : new Error(String(error)), { context: 'app_preview_capture', appKey });
          return { failure: FAILED };
        })
        .then((upload) => answerPreviewCapture(appKey, captureId, upload))
        .catch(() => undefined);
    },
    [appKey, look],
  );
};
